import { usePage, useForm } from '@inertiajs/react'

import { Button, TextInput } from '@atoms'
import Badge from '@components/atoms/Badge'
import { CsrfTokenField } from '@components/atoms/CsrfTokenField'
import Link from '@components/atoms/Link'
import { ConfigurationsLayout } from '@components/configurations/ConfigurationsLayout'
import { SettingsCard } from '@components/configurations/SettingsCard'
import { fieldErrors, SettingsForm, textField } from '@components/configurations/SettingsForm'
import { RadioGroupField } from '@components/molecules/RadioGroupField'
import { SelectField, type SelectFieldOption } from '@components/molecules/SelectField'
import cn from '@components/utils/cn'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('configurations.nation_builder')

type Values = { SLUG: string; PRIMARY_SITE_ID: string; BROADCASTER_ID: string; EXCLUDE_EVENT_TAG: string }

export type NationBuilderShowProps = {
  values: Values
  connected: boolean
  slugLocked: boolean
  completed: boolean
  primarySite: { slug: string; name: string } | null
  sites: SelectFieldOption<string>[]
  broadcastersUrl: string
}

const validBroadcasterId = (value: string) => Number.isFinite(Number(value)) && Math.trunc(Number(value)) !== 0

function ReadOnlyFields({ fields, className }: { fields: { label: string; value: string }[]; className?: string }) {
  return (
    <dl className={cn('tw:m-0 tw:flex tw:flex-col tw:gap-5', className)}>
      {fields.map(({ label, value }) => (
        <div key={label}>
          <dt className="tw:pb-1 tw:font-medium tw:text-gray-700">{label}</dt>
          <dd className="tw:m-0 tw:text-gray-cool-900">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

function NationForm({ values, connected, slugLocked, primarySite, sites, broadcastersUrl }: NationBuilderShowProps) {
  const form = useForm(values)
  const errors = fieldErrors(form.errors)
  const readOnly = [
    ...(slugLocked ? [{ label: t('slug'), value: values.SLUG }] : []),
    ...(connected && primarySite ? [{ label: t('primary_site'), value: primarySite.name || primarySite.slug }] : []),
  ]
  const setPrimarySite = (value: string) => form.setData('PRIMARY_SITE_ID', value)

  // A legacy-token nation that has not reconnected has nothing to edit, so no form and no Save.
  if (slugLocked && !connected) return <ReadOnlyFields fields={readOnly} className="tw:p-5" />

  return (
    <SettingsForm form={form} action="/app/settings/nation_builder">
      {readOnly.length > 0 && <ReadOnlyFields fields={readOnly} />}
      {!slugLocked && (
        <TextInput label={t('slug')} {...textField(form, 'SLUG')} />
      )}
      {connected && (
        <>
          {!primarySite &&
            (sites.length < 5 ? (
              <RadioGroupField
                label={t('primary_site')}
                options={sites}
                value={form.data.PRIMARY_SITE_ID || null}
                onValueChange={setPrimarySite}
              />
            ) : (
              <SelectField
                search
                name="PRIMARY_SITE_ID"
                label={t('primary_site')}
                options={sites}
                value={form.data.PRIMARY_SITE_ID}
                onValueChange={setPrimarySite}
                errors={errors}
              />
            ))}
          <div className="tw:flex tw:flex-col tw:gap-1">
            <TextInput
              label={t('broadcaster_id')}
              description={t(validBroadcasterId(values.BROADCASTER_ID) ? 'broadcaster_enabled' : 'broadcaster_missing')}
              {...textField(form, 'BROADCASTER_ID')}
            />
            <Link
              href={broadcastersUrl}
              target="_blank"
              rank="link"
              size="sm"
              trailingIcon="external-link"
              className="tw:p-0"
            >
              {t('broadcasters_link')}
            </Link>
          </div>
          <TextInput
            label={t('exclude_event_tag')}
            description={t('exclude_event_tag_hint')}
            {...textField(form, 'EXCLUDE_EVENT_TAG')}
          />
        </>
      )}
    </SettingsForm>
  )
}

export default function NationBuilderShow(props: NationBuilderShowProps) {
  const reconnectionRequired = usePage().props.reconnectionRequired as boolean

  return (
    <ConfigurationsLayout>
      <SettingsCard heading={t('heading')} description={props.completed ? undefined : t('pending')}>
        <NationForm {...props} />
      </SettingsCard>
      <SettingsCard heading={t('connection_heading')}>
        <form action="/nation_builder/connection" method="post" className="tw:flex tw:items-center tw:gap-3 tw:p-5">
          <CsrfTokenField />
          <Button type="submit" rank={reconnectionRequired ? 'primary' : 'secondary'}>
            {t('reconnect_nation')}
          </Button>
          {reconnectionRequired && <Badge color="warning">{t('reconnection_required')}</Badge>}
        </form>
      </SettingsCard>
      <SettingsCard heading={t('rsvp_form_heading')} description={t('rsvp_form_help')}>
        <div className="tw:p-5">
          <Link as="inertiaLink" href="/app/settings/embeds/rsvp_form" rank="secondary">
            {t('rsvp_form_link')}
          </Link>
        </div>
      </SettingsCard>
    </ConfigurationsLayout>
  )
}
